/**
 * Claims de los tokens emitidos por el IdP. Ver docs/FASE-0.md §4.
 */
import { z } from "zod";
import { CONSOLE_AUDIENCE, PATTERNS, PLATFORM_AUDIENCE } from "./constants.js";
/** Claims registrados comunes a todo JWT del IdP. */
const registeredClaimsSchema = z.object({
    iss: z.url(),
    sub: z.string().min(1),
    aud: z.string().min(1),
    exp: z.number().int().positive(),
    iat: z.number().int().positive(),
    jti: z.string().min(1),
});
/**
 * Access token de un usuario de una organización hacia un módulo (`aud` = código del módulo).
 * `org` es null cuando el usuario aún no eligió organización (solo `openid`/launcher).
 */
export const AccessTokenClaimsSchema = registeredClaimsSchema.extend({
    sub: z.uuid(),
    sid: z.uuid(),
    org: z.uuid().nullable(),
    org_slug: z.string().regex(PATTERNS.ORG_SLUG).nullable(),
    roles: z.array(z.string().regex(PATTERNS.KEY)).max(16),
    ent_v: z.number().int().nonnegative(),
    amr: z.array(z.enum(["pwd", "totp", "magic", "recovery"])).min(1),
    scope: z.string(),
});
/** Token de operador Continnua (consola). */
export const OperatorTokenClaimsSchema = registeredClaimsSchema.extend({
    sub: z.uuid(),
    aud: z.literal(CONSOLE_AUDIENCE),
    sid: z.uuid(),
    op_role: z.enum(["owner", "support", "billing"]),
    amr: z.array(z.enum(["pwd", "totp"])).min(1),
    scope: z.string(),
});
/** Token de un módulo (client credentials) hacia el API del núcleo. */
export const ModuleTokenClaimsSchema = registeredClaimsSchema.extend({
    sub: z.string().regex(/^client:[A-Za-z0-9_.-]+$/),
    aud: z.literal(PLATFORM_AUDIENCE),
    module: z.string().regex(PATTERNS.MODULE_CODE),
    scope: z.string(),
});
/** Cualquier token válido del IdP. */
export const AnyTokenClaimsSchema = z.union([
    OperatorTokenClaimsSchema,
    ModuleTokenClaimsSchema,
    AccessTokenClaimsSchema,
]);
/** Respuesta de `GET /oauth/userinfo`. */
export const UserInfoSchema = z.object({
    sub: z.uuid(),
    email: z.email(),
    email_verified: z.boolean(),
    name: z.string(),
    locale: z.string(),
    org: z.uuid().nullable(),
    org_slug: z.string().nullable(),
    updated_at: z.number().int(),
});
/** Respuesta estándar de `POST /oauth/token`. */
export const TokenResponseSchema = z.object({
    access_token: z.string(),
    token_type: z.literal("Bearer"),
    expires_in: z.number().int().positive(),
    refresh_token: z.string().optional(),
    id_token: z.string().optional(),
    scope: z.string(),
});
/** Error OAuth 2.0 (RFC 6749 §5.2). */
export const OAuthErrorSchema = z.object({
    error: z.enum([
        "invalid_request",
        "invalid_client",
        "invalid_grant",
        "unauthorized_client",
        "unsupported_grant_type",
        "invalid_scope",
        "access_denied",
        "server_error",
        "temporarily_unavailable",
    ]),
    error_description: z.string().optional(),
});
//# sourceMappingURL=claims.js.map